<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import ContactAPI from 'dashboard/api/contacts';

import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ReplyBox from 'dashboard/components/widgets/conversation/ReplyBox.vue';
import ResizableEditorWrapper from 'dashboard/components/widgets/conversation/ResizableEditorWrapper.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: Number, required: true },
  // Inbox of the newest message on the timeline, so the composer opens on the channel the
  // agent was just reading.
  preferredInboxId: { type: Number, default: null },
  containerHeight: { type: Number, default: 0 },
});

const emit = defineEmits(['update:conversation']);

const { t } = useI18n();
const store = useStore();

const inboxGetter = useMapGetter('inboxes/getInbox');
const conversationGetter = useMapGetter('getConversationById');

const conversations = ref([]);
const selectedInboxId = ref(null);
const isLoadingConversation = ref(false);
const isDropdownOpen = ref(false);
const hasPickedChannel = ref(false);
const resizableEditorWrapperRef = ref(null);

// The timeline merges every channel, so a reply has to name the one it goes out on. Each
// channel offers its most recent conversation - the endpoint already orders by activity.
const channels = computed(() => {
  const seen = new Set();
  return conversations.value.filter(conversation => {
    if (seen.has(conversation.inbox_id)) return false;
    seen.add(conversation.inbox_id);
    return true;
  });
});

const selectedChannel = computed(() =>
  channels.value.find(
    ({ inbox_id: inboxId }) => inboxId === selectedInboxId.value
  )
);

// The reply box works off the store, so it can only compose once the conversation is loaded
// there with its messages - the same state a conversation on screen would have.
const storeConversation = computed(() =>
  selectedChannel.value
    ? conversationGetter.value(selectedChannel.value.id)
    : undefined
);

const inbox = computed(() => inboxGetter.value(selectedInboxId.value));

const inboxName = computed(
  () => inbox.value.name || t('CONVERSATION_HISTORY.UNKNOWN_INBOX')
);

const menuItems = computed(() =>
  channels.value.map(conversation => {
    const channelInbox = inboxGetter.value(conversation.inbox_id);
    return {
      label: channelInbox.name || t('CONVERSATION_HISTORY.UNKNOWN_INBOX'),
      value: conversation.inbox_id,
      action: 'select',
      icon: getInboxIconByType(channelInbox.channel_type, channelInbox.medium),
      isSelected: conversation.inbox_id === selectedInboxId.value,
    };
  })
);

const selectChannel = ({ value }) => {
  hasPickedChannel.value = true;
  selectedInboxId.value = value;
  isDropdownOpen.value = false;
};

const loadConversation = async conversationId => {
  isLoadingConversation.value = true;
  try {
    await store.dispatch('getConversation', conversationId);
    // Same first load a conversation gets when it is opened (see setActiveChat): the show
    // payload carries only the last message, and quoted email replies and reply-to need the
    // rest. Paging from that message keeps the store free of duplicates.
    const conversation = conversationGetter.value(conversationId);
    if (conversation.messages.length <= 1) {
      await store.dispatch('fetchPreviousMessages', {
        conversationId,
        before: conversation.messages[0]?.id,
      });
    }
  } finally {
    isLoadingConversation.value = false;
  }
};

const fetchConversations = async () => {
  try {
    const { data } = await ContactAPI.getConversations(props.contactId);
    conversations.value = data.payload;
  } catch (error) {
    useAlert(t('CONVERSATION_HISTORY.COMPOSER.CHANNELS_ERROR'));
  }
};

watch(
  [channels, () => props.preferredInboxId],
  () => {
    // The channel list and the timeline load independently, so keep following the newest
    // message until the agent picks a channel themselves.
    if (hasPickedChannel.value || !channels.value.length) return;
    const preferred = channels.value.find(
      ({ inbox_id: inboxId }) => inboxId === props.preferredInboxId
    );
    selectedInboxId.value = (preferred ?? channels.value[0]).inbox_id;
  },
  { immediate: true }
);

watch(selectedChannel, channel => {
  if (channel) loadConversation(channel.id);
});

watch(storeConversation, conversation =>
  emit('update:conversation', conversation)
);

onMounted(fetchConversations);
</script>

<template>
  <div
    v-if="selectedChannel"
    class="relative flex flex-col flex-shrink-0 border-t border-n-weak bg-n-surface-1"
  >
    <div
      v-on-click-outside="() => (isDropdownOpen = false)"
      class="relative self-start px-4 pt-3"
    >
      <button
        v-tooltip.top="t('CONVERSATION_HISTORY.COMPOSER.SELECT_CHANNEL')"
        class="flex items-center h-8 gap-2 px-3 border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12 hover:border-n-slate-6"
        @click="isDropdownOpen = !isDropdownOpen"
      >
        <Icon
          :icon="getInboxIconByType(inbox.channel_type, inbox.medium)"
          class="flex-shrink-0 size-3.5"
        />
        <span class="text-sm truncate max-w-32">{{ inboxName }}</span>
        <span class="text-xs text-n-slate-10">
          {{ `#${selectedChannel.id}` }}
        </span>
        <span class="flex-shrink-0 size-3.5 i-lucide-chevron-down" />
      </button>
      <DropdownMenu
        v-if="isDropdownOpen"
        :menu-items="menuItems"
        class="ltr:left-4 rtl:right-4 bottom-12 max-h-56 w-fit max-w-xs"
        @action="selectChannel"
      />
    </div>
    <div v-if="isLoadingConversation" class="flex justify-center py-10">
      <Spinner class="text-n-brand" />
    </div>
    <ResizableEditorWrapper
      v-else-if="storeConversation"
      ref="resizableEditorWrapperRef"
      :container-height="containerHeight"
    >
      <ReplyBox
        :conversation="storeConversation"
        @toggle-editor-size="resizableEditorWrapperRef?.toggleEditorExpand?.()"
      />
    </ResizableEditorWrapper>
  </div>
</template>
